import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchFinanceFxSnapshot } from "../api/financeFxApi.js";
import { EMPTY_FX_RATES_META, buildFxRatesMeta } from "./fxRatesMeta.js";
import { logFxFetchFailed, logFxStaleFallback } from "./intlLogger.js";
import { getManifestI18n, isCurrencySupportedByManifest } from "./manifestI18n.js";
import { getI18nRollout } from "./i18nRollout.js";
import {
  getSupportedCurrencies,
  normalizeDisplayCurrency,
  readDisplayCurrencyFromStorage,
  resolveDisplayCurrency,
  writeDisplayCurrencyToStorage,
} from "./currencyResolution.js";
import { formatMoney as formatMoneyValue } from "./formatters.js";
import { useOptionalLocale } from "./LocaleProvider.jsx";
import { trackCurrencyChanged } from "../utils/automap/fxCurrencyAnalytics.js";

const CurrencyContext = createContext(null);

export function CurrencyProvider({
  initialDisplayCurrency,
  accountPreferences = null,
  manifest = null,
  children,
}) {
  const localeContext = useOptionalLocale();
  const locale = localeContext?.locale;
  const manifestI18n = useMemo(() => getManifestI18n(manifest), [manifest]);
  const rollout = useMemo(() => getI18nRollout(manifest), [manifest]);
  const supportedCurrencies = useMemo(() => {
    const currencies = getSupportedCurrencies(manifest);
    if (rollout.displayCurrencyEnabled === false) {
      return [manifestI18n.defaultCurrency || "COP"];
    }
    return currencies;
  }, [manifest, manifestI18n.defaultCurrency, rollout.displayCurrencyEnabled]);

  const [displayCurrency, setDisplayCurrencyState] = useState(() =>
    normalizeDisplayCurrency(
      initialDisplayCurrency ??
        resolveDisplayCurrency({
          storedCurrency: readDisplayCurrencyFromStorage(),
          accountPreferences,
          manifest,
          surface: manifest ? "clientView" : null,
        })
    )
  );

  const [rates, setRates] = useState({
    COP: 1,
    USD: 0.00025,
    EUR: 0.00023,
    RUB: 0.022,
    MXN: 0.0045,
  });
  const [ratesAvailable, setRatesAvailable] = useState(true);
  const [ratesMeta, setRatesMeta] = useState(() => ({ ...EMPTY_FX_RATES_META }));

  useEffect(() => {
    const resolved = normalizeDisplayCurrency(
      resolveDisplayCurrency({
        storedCurrency: readDisplayCurrencyFromStorage(),
        accountPreferences,
        manifest,
        surface: manifest ? "clientView" : null,
      })
    );
    setDisplayCurrencyState(resolved);
    if (manifest) {
      writeDisplayCurrencyToStorage(resolved);
    }
  }, [accountPreferences, manifest]);

  useEffect(() => {
    let cancelled = false;

    fetchFinanceFxSnapshot()
      .then((payload) => {
        if (cancelled) return;
        if (payload?.rates) {
          const hasConversionRates = Object.entries(payload.rates).some(
            ([code, value]) => code !== "COP" && Number(value) > 0
          );
          setRates(payload.rates);
          setRatesAvailable(hasConversionRates);
          const nextMeta = payload.meta
            ? { ...payload.meta }
            : buildFxRatesMeta(payload, {
                isFallback: payload.source === "fallback",
              });
          setRatesMeta(nextMeta);
          if (nextMeta.stale) {
            logFxStaleFallback(nextMeta.source);
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          logFxFetchFailed(error);
          setRates({
            COP: 1,
            USD: 0.00025,
            EUR: 0.00023,
            RUB: 0.022,
            MXN: 0.0045,
          });
          setRatesAvailable(true);
          setRatesMeta(
            buildFxRatesMeta({ source: "fallback", stale: false }, { isFallback: true })
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const setDisplayCurrency = useCallback(
    (nextCurrency, options = {}) => {
      const normalized = normalizeDisplayCurrency(nextCurrency);
      if (manifest && !isCurrencySupportedByManifest(normalized, manifestI18n)) {
        return;
      }
      if (normalized !== displayCurrency && options.trackChange !== false) {
        trackCurrencyChanged({
          fromCurrency: displayCurrency,
          toCurrency: normalized,
          rates,
          ratesMeta,
          ratesAvailable,
          sourceComponent: options.sourceComponent || null,
          surface: options.surface || null,
        });
      }
      setDisplayCurrencyState(normalized);
      writeDisplayCurrencyToStorage(normalized);
    },
    [manifest, manifestI18n, displayCurrency, rates, ratesMeta, ratesAvailable]
  );

  const formatMoney = useCallback(
    (amountCop, options = {}) =>
      formatMoneyValue(amountCop, {
        displayCurrency: rollout.fxConversionEnabled === false ? "COP" : displayCurrency,
        locale,
        rates,
        ratesAvailable: rollout.fxConversionEnabled !== false && ratesAvailable,
        ratesStale: ratesMeta?.stale,
        ...options,
      }),
    [displayCurrency, locale, rates, ratesAvailable, ratesMeta, rollout.fxConversionEnabled]
  );

  const value = useMemo(
    () => ({
      displayCurrency,
      supportedCurrencies,
      setDisplayCurrency,
      formatMoney,
      rates,
      ratesAvailable,
      ratesMeta,
    }),
    [displayCurrency, supportedCurrencies, setDisplayCurrency, formatMoney, rates, ratesAvailable, ratesMeta]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within CurrencyProvider");
  }
  return context;
}

export function useOptionalCurrency() {
  return useContext(CurrencyContext);
}
